/**
 * ActionCard.jsx — Renders proposed AI actions inline in chat messages.
 *
 * Displays a list of toggleable action cards that the user can approve
 * and execute. Once applied, shows a "done" state.
 */

import React, { useState } from 'react';
import { Check, Zap, ListChecks, Plus, CheckCircle } from 'lucide-react';
import useAICoachStore, { getActionLabel } from '../../stores/useAICoachStore';
import './ActionCard.css';

function getActionIcon(type) {
  switch (type) {
    case 'add_subtasks': return ListChecks;
    case 'create_task': return Plus;
    case 'complete_task': return CheckCircle;
    case 'prioritize_task': return Zap;
    default: return Zap;
  }
}

export default function ActionCard({ actions, actionsApplied, convId, msgIndex }) {
  const toggleAction = useAICoachStore(s => s.toggleAction);
  const applyActions = useAICoachStore(s => s.applyActions);
  const [isApplying, setIsApplying] = useState(false);

  if (!actions || actions.length === 0) return null;

  const selectedCount = actions.filter(a => a.selected).length;

  const handleApply = async () => {
    setIsApplying(true);
    await applyActions(convId, msgIndex);
    setIsApplying(false);
  };

  return (
    <div className={`action-card ${actionsApplied ? 'action-card--applied' : ''}`}>
      <div className="action-card__header">
        <Zap size={14} />
        <span>
          {actionsApplied
            ? 'Actions applied'
            : `${actions.length} proposed action${actions.length !== 1 ? 's' : ''}`
          }
        </span>
      </div>

      <div className="action-card__list">
        {actions.map((action) => {
          const Icon = getActionIcon(action.type);
          const label = getActionLabel(action);
          const isSelected = action.selected;

          return (
            <div key={action.id} className="action-card__row-wrapper">
              <div
                className={`action-card__row ${!isSelected ? 'action-card__row--dim' : ''} ${actionsApplied ? 'action-card__row--done' : ''}`}
                onClick={() => !actionsApplied && toggleAction(convId, msgIndex, action.id)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={actionsApplied}
                  readOnly
                  className="action-card__cb"
                />
                <Icon size={14} className="action-card__icon" />
                <span className="action-card__label">{label}</span>
              </div>

              {action.type === 'add_subtasks' && action.data?.subtasks?.length > 0 && (
                <div className="action-card__subtasks">
                  {action.data.subtasks.map((st, i) => (
                    <div key={i} className="action-card__subtask">
                      <span className="action-card__subtask-dot">•</span>
                      <span className="action-card__subtask-text">{st.title}</span>
                      {st.estimatedMinutes && (
                        <span className="action-card__subtask-time">~{st.estimatedMinutes}m</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!actionsApplied && (
        <button
          className="action-card__apply"
          onClick={handleApply}
          disabled={isApplying || selectedCount === 0}
        >
          {isApplying ? (
            'Applying...'
          ) : (
            <>
              <Check size={14} />
              Apply {selectedCount} action{selectedCount !== 1 ? 's' : ''}
            </>
          )}
        </button>
      )}
    </div>
  );
}
